import { FeatureFlags } from '@scaleup/models';
import { PropsWithChildren } from 'react';
import { useFeatureFlag, useLDFeatureFlag } from '../hooks';

interface ConditionalProps extends PropsWithChildren {
  condition: boolean | (() => boolean);
}

export const Conditional = ({ children, condition }: ConditionalProps) => {
  if (typeof condition === 'boolean') {
    return condition ? children : null;
  }

  if (typeof condition === 'function' && !condition()) {
    return null;
  }

  return children;
};

interface BooleanFeatureProps extends PropsWithChildren {
  featureFlag: keyof FeatureFlags;
  defaultValue: boolean;
  flipCondition?: boolean;
  // when ^ true the children will be returned when the value is false, instead of when the value is true
}

export const BooleanFeature = ({
  featureFlag,
  defaultValue,
  flipCondition = false,
  children,
}: BooleanFeatureProps) => {
  const val = useFeatureFlag(featureFlag, defaultValue);

  if (flipCondition && val === false) {
    return children;
  }

  if (flipCondition && val === true) {
    return null;
  }

  if (!flipCondition && val === false) {
    return null;
  }

  if (!flipCondition && val === true) {
    return children;
  }

  return null;
};

interface LDFeatureProps extends PropsWithChildren {
  featureFlag: string;
  defaultValue: boolean;
  flipCondition?: boolean;
  // when ^ true the children will be returned when the value is false, instead of when the value is true
}

export const LDFeature = ({
  featureFlag,
  defaultValue,
  flipCondition = false,
  children,
}: LDFeatureProps) => {
  const val = useLDFeatureFlag(featureFlag, defaultValue);

  if (flipCondition && val === false) {
    return children;
  }

  if (flipCondition && val === true) {
    return null;
  }

  if (!flipCondition && val === false) {
    return null;
  }

  if (!flipCondition && val === true) {
    return children;
  }

  return null;
};
